import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../ui/Button';
import { Dialog } from './AccountBits';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { FreightChoiceFields } from './FreightChoiceFields';
import { EMPTY_FREIGHT_CHOICE, freightChoiceFrom, type FreightChoiceDraft } from '../../../utils/freightChoice';

/**
 * Pops up on a draft invoice whose customer has no freight set, and again
 * before it is authorised by hand. "Use the zone" is one of the answers, so
 * nobody is stuck; closing it leaves the zone's figure on the draft.
 */
export function FreightChoiceDialog({ invoiceId, invoiceNumber, customerName, onClose, onSaved }: {
  invoiceId: number;
  invoiceNumber: string;
  customerName: string;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'FreightChoiceDialog' });
  const [draft, setDraft] = useState<FreightChoiceDraft>(EMPTY_FREIGHT_CHOICE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const choice = freightChoiceFrom(draft);

  const submit = async () => {
    if (!choice) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.setInvoiceFreightChoice(invoiceId, choice);
      if (res.success) {
        await onSaved();
        return;
      }
      setError(errorMessage(res, t('failed')));
    } catch (e) {
      setError(errorMessage(e, t('failed')));
    }
    setBusy(false);
  };

  return (
    <Dialog
      title={t('title')}
      subtitle={t('subtitle', { number: invoiceNumber })}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('later')}</Button>
          <Button onClick={submit} disabled={!choice || busy}>{busy ? t('saving') : t('confirm')}</Button>
        </>
      }
    >
      <p className="text-sm text-neutral-700 break-words">{t('body', { customer: customerName })}</p>
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}
      <FreightChoiceFields value={draft} onChange={setDraft} customerName={customerName} idPrefix="invoice-freight-choice" />
    </Dialog>
  );
}
